import { useState } from 'react';
import { ItemHandlingUnit, ItemReceipt } from '../../store/config/types';
import { Warehouse } from '../../store/config/types/warehouses.types';
import {
  Alert,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditLotIdLink from './EditLotIdLink';
import { convertConstantLiteralToNormal, formatDate, getUserDisplayName } from '../../utils/string.helpers';
import { HandlingUnitListDialog } from '../../components/HandlingUnitListDialog/HandlingUnitListDialog';
import { ArrowForward, Business, Close, Person, Print, Store, SyncDisabled } from '@material-ui/icons';
import StatusChip from '../../components/StatusChip/StatusChip';
import WarehouseEditLink from '../../components/WarehouseEditLink/WarehouseEditLink';
import VendorEditLink from '../../components/VendorEditLink/VendorEditLink';

interface ItemReceiptInfoProps {
  itemReceipt: ItemReceipt;
  disableActions?: boolean;
  warehouses: Warehouse[];
  printable?: boolean;
  // eslint-disable-next-line
  onOperation?: (operation: string, rows: any[]) => void;
}
export default function ItemReceiptInfo({
  itemReceipt,
  onOperation,
  disableActions,
  warehouses,
  printable,
}: ItemReceiptInfoProps) {
  const [selectedHandlingUnits, setSelectedHandlingUnits] = useState<ItemHandlingUnit[]>([]);
  const [openHandlingUnitsDialog, setOpenHandlingUnitsDialog] = useState(false);

  function handlePrint() {}

  return (
    <Grid container flexDirection={'column'} p={4} pt={1} gap={2}>
      <Grid item>
        <Grid container alignItems={'center'}>
          <Grid item flexGrow={1}>
            <Grid container gap={1} alignItems={'center'}>
              <Grid>Date: {formatDate(itemReceipt.receivedDate)}</Grid>
              <Grid>
                <StatusChip status={itemReceipt.status ?? ''} />
              </Grid>
              <Grid>
                <Grid container alignItems="flex-end">
                  <Person /> {getUserDisplayName(itemReceipt.warehouseUser)}
                </Grid>
              </Grid>
              {/* <Grid display="none">{lastRefresh?.getTime()}</Grid> */}
            </Grid>
          </Grid>
          <Grid item>
            {printable && (
              <IconButton onClick={handlePrint} title="Print">
                <Print />
              </IconButton>
            )}
            <IconButton
              onClick={() => {
                onOperation?.('close-current-ir', [itemReceipt.itemReceiptId]);
              }}
            >
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container alignItems="center" gap={1}>
          <Business />
          <VendorEditLink
            disabled={disableActions}
            vendorId={itemReceipt.vendorId}
            vendorName={itemReceipt.vendor}
            onSubmit={(vendorId, vendorName) =>
              onOperation?.('set-item-receipt-vendor', [
                {
                  vendorId,
                  vendor: vendorName,
                  itemReceiptId: itemReceipt.itemReceiptId,
                },
              ])
            }
          />
          <Grid ml={1} mr={1}>
            <ArrowForward style={{ color: 'gray' }} />
          </Grid>
          <Store />
          <WarehouseEditLink
            disabled={disableActions}
            warehouseId={itemReceipt.warehouseId}
            warehouses={warehouses ?? []}
            onSubmit={(warehouseId) =>
              onOperation?.('set-item-receipt-warehouse', [
                {
                  warehouseId,
                  itemReceiptId: itemReceipt?.itemReceiptId,
                },
              ])
            }
          />
        </Grid>
      </Grid>
      <Grid item>
        <Table>
          <TableHead>
            <TableCell>ID</TableCell>
            <TableCell>Product</TableCell>
            <TableCell>Quantity</TableCell>
            <TableCell>Lot</TableCell>
          </TableHead>
          <TableBody>
            {itemReceipt.items.map((item, index) => (
              <TableRow key={`product-${index}`}>
                <TableCell>{item.product?.externalId ?? item.productId}</TableCell>
                <TableCell>
                  <Typography variant="body1">{item.product?.name}</Typography>
                  <Typography variant="caption" color="gray">
                    {item.product?.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Grid
                    container
                    flexDirection="row"
                    alignItems="end"
                    gap={1}
                    onClick={() => {
                      if (item.handlingUnits?.length) {
                        setSelectedHandlingUnits(item.handlingUnits);
                        setOpenHandlingUnitsDialog(true);
                      }
                    }}
                    style={{ cursor: item.handlingUnits?.length ? 'pointer' : 'default' }}
                  >
                    <Typography
                      variant="body1"
                      style={{
                        textDecoration: item.handlingUnits?.length ? 'underline' : 'none',
                        color: item.handlingUnits?.length ? 'blue' : 'inherit',
                      }}
                    >
                      {item.quantity.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="gray">
                      {item.product?.unitOfMeasure == 'Each' ? '' : item.product?.unitOfMeasure}
                    </Typography>
                  </Grid>
                </TableCell>

                <TableCell>
                  <EditLotIdLink
                    currentLotId={item.lotId}
                    dialogTitle="Change Lot Id"
                    product={item.product}
                    warehouses={warehouses ?? []}
                    warehouseId={itemReceipt.warehouseId}
                    disabled={['SYNCED'].includes(itemReceipt.status ?? '')}
                    onSubmit={(newLotId, warehouseId) =>
                      onOperation &&
                      onOperation('set-item-receipt-item-lot-id', [
                        {
                          lotId: newLotId,
                          warehouseId,
                          itemReceiptId: itemReceipt.itemReceiptId,
                          itemReceiptItemId: item.itemReceiptItemId,
                        },
                      ])
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid item>
        <Grid container justifyContent={'end'} gap={2}>
          {itemReceipt.status == 'PENDING_APPROVAL' && (
            <>
              <Button
                color="error"
                variant="outlined"
                disabled={disableActions}
                onClick={() => {
                  if (onOperation)
                    onOperation('item-receipts-reject', [
                      {
                        itemReceiptId: itemReceipt.itemReceiptId,
                        status: convertConstantLiteralToNormal(itemReceipt.status),
                        itemReceipt,
                      },
                    ]);
                }}
              >
                Reject & REOPEN
              </Button>
              <Button
                color="success"
                variant="outlined"
                disabled={disableActions}
                onClick={() => {
                  if (onOperation)
                    onOperation('item-receipts-approve', [
                      {
                        itemReceiptId: itemReceipt.itemReceiptId,
                        status: convertConstantLiteralToNormal(itemReceipt.status),
                        itemReceipt,
                      },
                    ]);
                }}
              >
                Approve
              </Button>
            </>
          )}
          {itemReceipt.status == 'APPROVED' && (
            <Button
              color="success"
              variant="outlined"
              disabled={disableActions}
              onClick={() => {
                if (onOperation)
                  onOperation('item-receipts-try-sync', [
                    {
                      itemReceiptId: itemReceipt.itemReceiptId,
                      status: convertConstantLiteralToNormal(itemReceipt.status),
                    },
                  ]);
              }}
            >
              Sync
            </Button>
          )}
          {itemReceipt.status == 'SYNC_FAILED' && (
            <Grid container flexDirection="column" gap={1}>
              <Grid item flexGrow={1}>
                {itemReceipt.additionalData?.errors?.map((error: string, errIndex: number) => (
                  <Alert variant="standard" color="error" key={errIndex} title="test" icon={<SyncDisabled />}>
                    {error}
                  </Alert>
                ))}
              </Grid>
              <Grid item>
                <Grid container justifyContent="end">
                  <Button
                    color="warning"
                    variant="outlined"
                    disabled={disableActions}
                    onClick={() => {
                      if (onOperation)
                        onOperation('item-receipts-try-sync', [
                          {
                            itemReceiptId: itemReceipt.itemReceiptId,
                            status: convertConstantLiteralToNormal(itemReceipt.status),
                          },
                        ]);
                    }}
                  >
                    Retry Sync
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <HandlingUnitListDialog
        open={openHandlingUnitsDialog}
        onClose={() => setOpenHandlingUnitsDialog(false)}
        handlingUnits={selectedHandlingUnits}
      />
    </Grid>
  );
}
